// reliableServiceWorker.js

/**
 * Provides more reliable service worker registration for iOS devices
 * and handles common edge cases and errors better than the default approach.
 */
import { isIOSSimulator, getIOSVersion } from './ios-simulator-detection';

// Cache keys for storing service worker status
const SW_REGISTRATION_STATUS = 'sw_registration_status';
const SW_LAST_UPDATE_CHECK = 'sw_last_update_check';

/**
 * Register a service worker with enhanced error handling and iOS compatibility
 * 
 * @param {string} swPath - Path to the service worker file
 * @param {Object} options - Registration options
 * @param {Object} callbacks - Callback functions
 * @returns {Promise<ServiceWorkerRegistration|null>} - The registration or null if registration failed
 */
export const registerServiceWorkerSafely = async (
  swPath, 
  options = {}, 
  callbacks = {}
) => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers are not supported in this browser');
    if (callbacks.onUnsupported) callbacks.onUnsupported('Service Worker API not supported');
    return null;
  }
  
  // Check for iOS simulator - service workers have issues in the simulator
  if (isIOSSimulator()) {
    console.log('iOS simulator detected - service worker registration may be unreliable');
    if (callbacks.onWarning) callbacks.onWarning('iOS simulator environment may not support service workers properly');
  }
  
  // Add timestamp to prevent caching on iOS
  const swUrl = `${swPath}?cachebust=${Date.now()}`;
  
  // Set iOS-specific options
  const iosVersion = getIOSVersion();
  const isIOS = !!iosVersion;
  
  // For iOS, we need to use specific options
  if (isIOS) {
    options = {
      ...options,
      // On iOS, setting this to 'none' ensures we don't use cached service workers
      updateViaCache: 'none',
    };
  }
  
  try {
    // Default scope to root if not specified
    if (!options.scope) {
      options.scope = '/';
    }
    
    // First check if this service worker is already registered
    const existingRegistrations = await navigator.serviceWorker.getRegistrations();
    let existingRegistration = null;
    
    for (const reg of existingRegistrations) {
      try {
        if (reg.active && reg.active.scriptURL.includes(swPath)) {
          existingRegistration = reg;
          console.log('Found existing service worker registration:', reg.scope);
          break;
        }
      } catch (err) {
        console.error('Error checking existing registration:', err);
      }
    }
    
    // Use the existing registration if found
    if (existingRegistration) {
      if (callbacks.onSuccess) callbacks.onSuccess(existingRegistration);
      
      // Check if we should update the service worker
      const lastUpdateCheck = localStorage.getItem(SW_LAST_UPDATE_CHECK);
      const shouldCheckForUpdate = !lastUpdateCheck || 
        (Date.now() - parseInt(lastUpdateCheck, 10)) > 3600000; // Check once per hour
      
      if (shouldCheckForUpdate) {
        console.log('Checking for service worker updates...');
        try {
          await existingRegistration.update();
          localStorage.setItem(SW_LAST_UPDATE_CHECK, Date.now().toString());
          console.log('Service worker update check completed');
        } catch (updateErr) {
          console.error('Error updating existing service worker:', updateErr);
        }
      }
      
      return existingRegistration;
    }
    
    // Otherwise, register a new service worker
    console.log('Registering new service worker:', swUrl);
    const registration = await navigator.serviceWorker.register(swUrl, options);
    console.log('Service worker registered successfully:', registration.scope);
    
    // Store the status
    try {
      localStorage.setItem(SW_REGISTRATION_STATUS, JSON.stringify({
        registered: true,
        timestamp: Date.now(),
        scope: registration.scope
      }));
      localStorage.setItem(SW_LAST_UPDATE_CHECK, Date.now().toString());
    } catch (storageErr) {
      console.error('Error storing service worker status:', storageErr);
    }
    
    // If installing, wait for it to be ready on iOS
    if (isIOS && registration.installing) {
      console.log('Waiting for service worker to be activated on iOS...');
      
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Service worker activation timed out'));
          }, 10000); // 10 second timeout
          
          const stateChangeListener = (event) => {
            if (event.target.state === 'activated') {
              clearTimeout(timeout);
              registration.installing.removeEventListener('statechange', stateChangeListener);
              resolve();
            }
          };
          
          registration.installing.addEventListener('statechange', stateChangeListener);
        });
        console.log('Service worker activated successfully on iOS');
      } catch (activationErr) {
        console.error('Error waiting for service worker activation:', activationErr);
        // Continue anyway, might work partially
      }
    }
    
    // Set up update handling
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      
      if (!installingWorker) return;
      
      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New service worker available
            console.log('New service worker installed and waiting');
            if (callbacks.onUpdate) callbacks.onUpdate(registration);
          } else {
            // First service worker installed
            console.log('Service worker installed for the first time');
            if (callbacks.onSuccess) callbacks.onSuccess(registration);
          }
        }
      };
    };
    
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    
    // Store the failure
    try {
      localStorage.setItem(SW_REGISTRATION_STATUS, JSON.stringify({
        registered: false,
        error: error.message,
        timestamp: Date.now()
      }));
    } catch (storageErr) {
      console.error('Error storing service worker status:', storageErr);
    }
    
    if (callbacks.onError) callbacks.onError(error);
    
    // For iOS, try a different approach as a fallback
    if (isIOS) {
      console.log('Trying alternative service worker registration for iOS...');
      try {
        // For iOS, sometimes registering without options works better
        const fallbackReg = await navigator.serviceWorker.register(swUrl);
        console.log('Alternative iOS service worker registration succeeded:', fallbackReg.scope);
        if (callbacks.onSuccess) callbacks.onSuccess(fallbackReg);
        return fallbackReg;
      } catch (iosError) {
        console.error('Alternative iOS service worker registration failed:', iosError);
        if (callbacks.onError) callbacks.onError(iosError);
      }
    }
    
    return null;
  }
};

/**
 * Activates a waiting service worker and reloads the page
 * @param {ServiceWorkerRegistration} registration - The service worker registration
 * @returns {Promise<void>}
 */
export const activateServiceWorker = async (registration) => {
  if (!registration || !registration.waiting) {
    console.log('No waiting service worker to activate');
    return;
  }
  
  try {
    // Send message to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Listen for the controlling service worker to change
    await new Promise((resolve) => {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('New service worker activated');
        resolve();
      });
    });
    
    // Reload the page to use the new service worker
    window.location.reload();
  } catch (error) {
    console.error('Error activating service worker:', error);
  }
};

/**
 * Checks if a service worker is registered and active
 * @param {string} swPath - Optional path to check for specific service worker
 * @returns {Promise<boolean>} - True if service worker is active
 */
export const isServiceWorkerActive = async (swPath = null) => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length === 0) {
      return false;
    }
    
    if (!swPath) {
      // Just check if any service worker is active
      return registrations.some(reg => !!reg.active);
    }
    
    // Check for specific service worker
    return registrations.some(reg => 
      reg.active && reg.active.scriptURL.includes(swPath)
    );
  } catch (error) {
    console.error('Error checking service worker status:', error);
    return false;
  }
};

/**
 * Unregisters all service workers
 * @returns {Promise<boolean>} - True if all service workers were unregistered
 */
export const unregisterAllServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    return false;
  }
  
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    if (registrations.length === 0) {
      return true;
    }
    
    const results = await Promise.all(
      registrations.map(registration => registration.unregister())
    );
    
    const allUnregistered = results.every(result => result === true);
    
    if (allUnregistered) {
      localStorage.removeItem(SW_REGISTRATION_STATUS);
      localStorage.removeItem(SW_LAST_UPDATE_CHECK);
    }
    
    return allUnregistered;
  } catch (error) {
    console.error('Error unregistering service workers:', error);
    return false;
  }
};

export default {
  registerServiceWorkerSafely,
  activateServiceWorker,
  isServiceWorkerActive,
  unregisterAllServiceWorkers
};